import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Atom, BarChart3, BrainCircuit, Check, ChevronDown,
  Clock, Copy, Cpu, Database, Loader2, Menu, MessageSquare, Plus,
  Radio, Search, Send, ShieldCheck, Sparkles, Square, Trash2, X,
} from "lucide-react";
import StatsArenaPage from "./StatsArenaPage";
import QuantumPhysicsPage from "./QuantumPhysicsPage";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${API_URL}/api`;
const STORAGE_KEY = "aeon_review_threads_v1";

const MODEL_OPTIONS = [
  {
    id: "claude",
    provider: "claude",
    name: "Claude",
    version: "Sonnet 5",
    badge: "Recommended",
    description: "Deep synthesis, counter-theses, and nuanced risk review.",
    icon: BrainCircuit,
  },
  {
    id: "claude_deep",
    provider: "claude",
    name: "Claude Deep",
    version: "Fable 5",
    badge: "Maximum depth",
    description: "Highest-effort reasoning for difficult regimes and post-trade review.",
    icon: Sparkles,
  },
  {
    id: "deepseek",
    provider: "deepseek",
    name: "DeepSeek",
    version: "V4 Pro",
    badge: "Second opinion",
    description: "Quant-heavy reasoning and an independent read of the evidence.",
    icon: Atom,
  },
  {
    id: "local",
    provider: "local",
    name: "Local",
    version: "DeepSeek 8B",
    badge: "Private",
    description: "Fast, on-device help for routine desk questions.",
    icon: Cpu,
  },
];

const DEFAULT_MODEL = "claude";
const MODEL_IDS = new Set(MODEL_OPTIONS.map((model) => model.id));

const STARTERS = [
  {
    eyebrow: "Performance",
    title: "Review today’s decisions",
    prompt: "Review today’s trading decisions. Separate verified evidence from assumptions, then tell me what deserves attention first.",
  },
  {
    eyebrow: "Market",
    title: "Tell me what changed",
    prompt: "What changed in the market since the last update, and which changes are actually relevant to our current positioning?",
  },
  {
    eyebrow: "Risk",
    title: "Challenge the current posture",
    prompt: "Challenge the current risk posture. Give me the strongest counterargument and the evidence that would change your conclusion.",
  },
  {
    eyebrow: "Strategies",
    title: "Find the weak link",
    prompt: "Which strategy or engine needs review first? Explain the evidence, uncertainty, and the next useful action.",
  },
];

const CONTEXT_OPTIONS = [
  { id: "market", label: "Market", icon: Radio },
  { id: "portfolio", label: "Portfolio", icon: ShieldCheck },
  { id: "system", label: "System", icon: Database },
];

function makeId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `review-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function makeThread(model = DEFAULT_MODEL) {
  const now = new Date().toISOString();
  return {
    id: makeId(),
    title: "New review",
    model: MODEL_IDS.has(model) ? model : DEFAULT_MODEL,
    createdAt: now,
    updatedAt: now,
    backendThreadId: null,
    messages: [],
  };
}

function loadThreads() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(value)) return [];
    return value
      .filter((thread) => thread && thread.id && Array.isArray(thread.messages))
      .map((thread) => ({
        ...thread,
        model: MODEL_IDS.has(thread.model) ? thread.model : DEFAULT_MODEL,
      }))
      .slice(0, 30);
  } catch {
    return [];
  }
}

function relativeTime(value) {
  if (!value) return "";
  const delta = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(delta / 60000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(value).toLocaleDateString([], { month: "short", day: "numeric" });
}

function messageTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function MindPage() {
  const initial = useRef(null);
  if (!initial.current) {
    const stored = loadThreads();
    const threads = stored.length ? stored : [makeThread()];
    initial.current = { threads, activeId: threads[0].id };
  }

  const [threads, setThreads] = useState(initial.current.threads);
  const [activeId, setActiveId] = useState(initial.current.activeId);
  const [mode, setMode] = useState("chat");
  const [query, setQuery] = useState("");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [contexts, setContexts] = useState(["market", "portfolio", "system"]);
  const [copiedId, setCopiedId] = useState(null);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);
  const modelPickerRef = useRef(null);

  const activeThread = threads.find((thread) => thread.id === activeId) || threads[0];
  const selectedModel = MODEL_OPTIONS.find((model) => model.id === activeThread?.model)
    || MODEL_OPTIONS[0];

  const filteredThreads = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const sorted = [...threads].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    if (!needle) return sorted;
    return sorted.filter((thread) =>
      `${thread.title} ${thread.messages.map((message) => message.content).join(" ")}`
        .toLowerCase()
        .includes(needle)
    );
  }, [threads, query]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(threads.slice(0, 30)));
    } catch {
      // Local history is a convenience; chat continues if storage is unavailable.
    }
  }, [threads]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [activeThread?.messages, sending]);

  useEffect(() => {
    if (!sending) {
      setElapsed(0);
      return undefined;
    }
    const timer = setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [sending]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.style.height = "auto";
    inputRef.current.style.height = `${Math.min(180, inputRef.current.scrollHeight)}px`;
  }, [input]);

  useEffect(() => {
    if (!modelMenuOpen) return undefined;
    function closeModelMenu(event) {
      if (!modelPickerRef.current?.contains(event.target)) setModelMenuOpen(false);
    }
    function closeModelMenuWithKeyboard(event) {
      if (event.key === "Escape") setModelMenuOpen(false);
    }
    document.addEventListener("pointerdown", closeModelMenu);
    document.addEventListener("keydown", closeModelMenuWithKeyboard);
    return () => {
      document.removeEventListener("pointerdown", closeModelMenu);
      document.removeEventListener("keydown", closeModelMenuWithKeyboard);
    };
  }, [modelMenuOpen]);

  function createConversation() {
    const next = makeThread(selectedModel.id);
    setThreads((current) => [next, ...current]);
    setActiveId(next.id);
    setInput("");
    setMode("chat");
    setSidebarOpen(false);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  function chooseThread(id) {
    setActiveId(id);
    setMode("chat");
    setSidebarOpen(false);
  }

  function deleteThread(event, id) {
    event.stopPropagation();
    setThreads((current) => {
      const remaining = current.filter((thread) => thread.id !== id);
      if (remaining.length) {
        if (id === activeId) setActiveId(remaining[0].id);
        return remaining;
      }
      const replacement = makeThread();
      setActiveId(replacement.id);
      return [replacement];
    });
  }

  function toggleContext(id) {
    setContexts((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  }

  function selectModel(modelId) {
    if (!MODEL_IDS.has(modelId) || sending) return;
    setThreads((current) => current.map((thread) =>
      thread.id === activeThread?.id ? { ...thread, model: modelId } : thread
    ));
    setModelMenuOpen(false);
  }

  async function sendMessage(override) {
    const text = String(override ?? input).trim();
    if (!text || sending || !activeThread) return;

    const targetId = activeThread.id;
    const now = new Date().toISOString();
    const userMessage = { id: makeId(), role: "user", content: text, createdAt: now };
    setInput("");
    setSending(true);
    setElapsed(0);

    setThreads((current) => current.map((thread) => {
      if (thread.id !== targetId) return thread;
      const title = thread.title === "New review"
        ? `${text.slice(0, 46)}${text.length > 46 ? "…" : ""}`
        : thread.title;
      return {
        ...thread,
        title,
        updatedAt: now,
        messages: [...thread.messages, userMessage],
      };
    }));

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const route = new URLSearchParams({
        provider: selectedModel.provider,
        model: selectedModel.id,
      });
      const response = await fetch(`${API}/team/chat?${route.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          specialist: "AEON",
          message: text,
          thread_id: activeThread.backendThreadId,
          context: `Review workspace. Attached context: ${contexts.length ? contexts.join(", ") : "none"}. Requested route: ${selectedModel.name} ${selectedModel.version}.`,
          quick: false,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.error) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }

      const reply = {
        id: makeId(),
        role: "assistant",
        content: data.reply || "I completed the review but received an empty response.",
        createdAt: data.ts || new Date().toISOString(),
        model: selectedModel.id,
      };
      setThreads((current) => current.map((thread) =>
        thread.id === targetId
          ? {
              ...thread,
              backendThreadId: data.thread_id || thread.backendThreadId,
              updatedAt: reply.createdAt,
              messages: [...thread.messages, reply],
            }
          : thread
      ));
    } catch (error) {
      if (error.name !== "AbortError") {
        const failure = {
          id: makeId(),
          role: "assistant",
          error: true,
          content: `I couldn’t complete that review. ${error.message || "Check the local Aeon connection and try again."}`,
          createdAt: new Date().toISOString(),
        };
        setThreads((current) => current.map((thread) =>
          thread.id === targetId
            ? { ...thread, updatedAt: failure.createdAt, messages: [...thread.messages, failure] }
            : thread
        ));
      }
    } finally {
      abortRef.current = null;
      setSending(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  function stopResponse() {
    abortRef.current?.abort();
  }

  async function copyMessage(message) {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopiedId(message.id);
      window.setTimeout(() => setCopiedId(null), 1400);
    } catch {
      // Clipboard access can be blocked outside localhost/HTTPS.
    }
  }

  function handleComposerKey(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  }

  const hasMessages = Boolean(activeThread?.messages?.length);
  const SelectedModelIcon = selectedModel.icon;

  return (
    <div className="review-ai-shell">
      <aside className={`review-sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="review-sidebar-head">
          <div className="review-sidebar-title">
            <span>AEON / 06</span>
            <strong>Review</strong>
          </div>
          <button type="button" className="review-sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="Close sidebar">
            <X />
          </button>
        </div>

        <button type="button" className="review-new-chat" onClick={createConversation}>
          <Plus />
          <span>New conversation</span>
        </button>

        <label className="review-search">
          <Search />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search conversations"
          />
        </label>

        <nav className="review-tool-nav" aria-label="Review tools">
          <span>Workspace</span>
          <button type="button" className={mode === "chat" ? "is-active" : ""} onClick={() => setMode("chat")}>
            <MessageSquare />
            <span>Conversation</span>
          </button>
          <button type="button" className={mode === "performance" ? "is-active" : ""} onClick={() => setMode("performance")}>
            <BarChart3 />
            <span>Performance</span>
          </button>
          <button type="button" className={mode === "signals" ? "is-active" : ""} onClick={() => setMode("signals")}>
            <Atom />
            <span>Signal model</span>
          </button>
        </nav>

        <div className="review-history">
          <div className="review-history-label">
            <span>Recent</span>
            <small>{threads.length}</small>
          </div>
          <div className="review-thread-list">
            {filteredThreads.map((thread) => (
              <button
                key={thread.id}
                type="button"
                className={thread.id === activeId && mode === "chat" ? "is-active" : ""}
                onClick={() => chooseThread(thread.id)}
              >
                <MessageSquare />
                <span>
                  <strong>{thread.title}</strong>
                  <small>{relativeTime(thread.updatedAt)}</small>
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  className="review-thread-delete"
                  aria-label={`Delete ${thread.title}`}
                  onClick={(event) => deleteThread(event, thread.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") deleteThread(event, thread.id);
                  }}
                >
                  <Trash2 />
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="review-sidebar-foot">
          <span><ShieldCheck /> Local history</span>
          <small>Private to this browser</small>
        </div>
      </aside>

      {sidebarOpen ? <button type="button" className="review-sidebar-backdrop" onClick={() => setSidebarOpen(false)} aria-label="Close sidebar" /> : null}

      <main className="review-main">
        <header className="review-topbar">
          <div className="review-topbar-title">
            <button type="button" className="review-menu-button" onClick={() => setSidebarOpen(true)} aria-label="Open sidebar">
              <Menu />
            </button>
            <div>
              <span>{mode === "chat" ? "Conversation" : "Review tool"}</span>
              <strong>
                {mode === "chat"
                  ? activeThread?.title || "New review"
                  : mode === "performance"
                    ? "Performance"
                    : "Signal model"}
              </strong>
            </div>
          </div>
          <div className="review-topbar-status">
            <span><i /> AEON ready</span>
            <small>{contexts.length} context sources</small>
          </div>
          <div className="review-model-picker" ref={modelPickerRef}>
            <button
              type="button"
              className={`review-model-trigger ${modelMenuOpen ? "is-open" : ""}`}
              onClick={() => setModelMenuOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={modelMenuOpen}
            >
              <SelectedModelIcon />
              <span>
                <strong>{selectedModel.name}</strong>
                <small>{selectedModel.version}</small>
              </span>
              <ChevronDown />
            </button>
            {modelMenuOpen ? (
              <div className="review-model-menu" role="listbox" aria-label="Choose AI model">
                <header>
                  <span>Reasoning model</span>
                  <small>Saved with this conversation</small>
                </header>
                {MODEL_OPTIONS.map((model) => {
                  const Icon = model.icon;
                  const selected = model.id === selectedModel.id;
                  return (
                    <button
                      key={model.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={selected ? "is-selected" : ""}
                      onClick={() => selectModel(model.id)}
                      disabled={sending}
                    >
                      <Icon />
                      <span>
                        <strong>{model.name} <small>{model.version}</small></strong>
                        <em>{model.description}</em>
                      </span>
                      <i>{selected ? <Check /> : model.badge}</i>
                    </button>
                  );
                })}
                <footer>Use live market context. Models do not have a trustworthy live tape on their own.</footer>
              </div>
            ) : null}
          </div>
          <button type="button" className="review-mobile-new" onClick={createConversation} aria-label="New conversation">
            <Plus />
          </button>
        </header>

        {mode === "chat" ? (
          <>
            <div className={`review-conversation ${hasMessages ? "has-messages" : "is-empty"}`}>
              {!hasMessages ? (
                <section className="review-welcome">
                  <div className="review-aeon-mark" aria-hidden>A<i /></div>
                  <span className="review-welcome-kicker">A private room for clear thinking</span>
                  <h1>What are we reviewing?</h1>
                  <p>
                    Ask Aeon to explain a decision, challenge an assumption, compare evidence,
                    or turn the live desk into a useful next step.
                  </p>
                  <div className="review-starters">
                    {STARTERS.map((starter, index) => (
                      <button key={starter.title} type="button" onClick={() => {
                        setInput(starter.prompt);
                        window.setTimeout(() => inputRef.current?.focus(), 0);
                      }}>
                        <span>{String(index + 1).padStart(2, "0")} / {starter.eyebrow}</span>
                        <strong>{starter.title}</strong>
                        <p>{starter.prompt}</p>
                      </button>
                    ))}
                  </div>
                </section>
              ) : (
                <div className="review-message-list">
                  {activeThread.messages.map((message) => (
                    <article key={message.id} className={`review-message is-${message.role} ${message.error ? "is-error" : ""}`}>
                      <div className="review-message-avatar" aria-hidden>
                        {message.role === "assistant" ? "A" : "T"}
                      </div>
                      <div className="review-message-body">
                        <header>
                          <strong>
                            {message.role === "assistant" ? "AEON" : "You"}
                            {message.role === "assistant" && message.model
                              ? <small>{MODEL_OPTIONS.find((model) => model.id === message.model)?.name}</small>
                              : null}
                          </strong>
                          <time>{messageTime(message.createdAt)}</time>
                        </header>
                        <ResponseText content={message.content} />
                        {message.role === "assistant" ? (
                          <div className="review-message-actions">
                            <button type="button" onClick={() => copyMessage(message)}>
                              {copiedId === message.id ? <Check /> : <Copy />}
                              {copiedId === message.id ? "Copied" : "Copy"}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))}
                  {sending ? (
                    <article className="review-message is-assistant is-thinking">
                      <div className="review-message-avatar" aria-hidden>A</div>
                      <div className="review-message-body">
                        <header>
                          <strong>AEON <small>{selectedModel.name}</small></strong>
                          <time>{elapsed}s</time>
                        </header>
                        <div className="review-thinking-line">
                          <Loader2 />
                          <span>{elapsed < 8 ? "Reading the live context" : "Working through the evidence"}</span>
                        </div>
                      </div>
                    </article>
                  ) : null}
                  <div ref={bottomRef} />
                </div>
              )}
            </div>

            <div className="review-composer-wrap">
              <div className="review-context-row">
                <span>Use context</span>
                {CONTEXT_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const active = contexts.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={active ? "is-active" : ""}
                      aria-pressed={active}
                      onClick={() => toggleContext(option.id)}
                    >
                      <Icon />
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <div className="review-composer">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={handleComposerKey}
                  placeholder="Message AEON…"
                  aria-label="Message AEON"
                />
                <div className="review-composer-foot">
                  <span>Enter to send · Shift + Enter for a new line</span>
                  {sending ? (
                    <button type="button" className="review-stop" onClick={stopResponse} aria-label="Stop response">
                      <Square />
                    </button>
                  ) : (
                    <button type="button" className="review-send" onClick={() => sendMessage()} disabled={!input.trim()} aria-label="Send message">
                      <Send />
                    </button>
                  )}
                </div>
              </div>
              <small className="review-disclaimer">Aeon can be wrong. Verify consequential decisions against live market data.</small>
            </div>
          </>
        ) : (
          <div className="review-tool-view">
            <div className="review-tool-heading">
              <button type="button" onClick={() => setMode("chat")}><ArrowLeft /> Conversation</button>
              <span>{mode === "performance" ? "Evidence and outcomes" : "Regime and signal research"}</span>
              <h1>{mode === "performance" ? "Performance review" : "Signal model"}</h1>
              <p>
                {mode === "performance"
                  ? "The original Aeon analytics remain here when the conversation needs a deeper source view."
                  : "Inspect the model layer without leaving your review workspace."}
              </p>
            </div>
            <div className="review-tool-content">
              {mode === "performance" ? <StatsArenaPage /> : <QuantumPhysicsPage />}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ResponseText({ content }) {
  const blocks = String(content || "").split(/\n{2,}/).filter(Boolean);
  return (
    <div className="review-response-text">
      {blocks.map((block, index) => {
        const lines = block.split("\n");
        const isList = lines.every((line) => /^\s*[-*•]\s+/.test(line));
        if (isList) {
          return (
            <ul key={index}>
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>{line.replace(/^\s*[-*•]\s+/, "")}</li>
              ))}
            </ul>
          );
        }
        return <p key={index}>{block}</p>;
      })}
    </div>
  );
}
